import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { checkValidNumber } from '@src/services/checkValid';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { VALID_SPEED } from '@src/constants/tts';
import { COLOR, TRANSPARENT_COLOR } from '@src/styles/color';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import { Box, Popover, TextField } from '@mui/material';
import { SPEED } from '@src/constants/voice';
import { replaceNumberWithDot } from '@src/utils/number';
import {
  PopoverMenuItem,
  StyledCustomSpeed,
  StyledListMenuItem,
  StyledSpeed,
} from './index.style';

const SpeedNew = ({
  currentSpeed = 1,
  isSentence,
  width,
  disabled,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const getTextValue = (value) => {
    if (value <= 0) return t('chooseSpeed');
    return `${value}x`;
  };

  const [speed, setSpeed] = useState();
  const [textValue, setTextValue] = useState(getTextValue(currentSpeed));
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);

  const inputRef = useRef(null);

  const handleCloseSpeed = (value) => {
    setSpeed('');
    setTextValue(getTextValue(value));
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleSelectedValue = (value) => {
    if (onChange) onChange(value);
    handleCloseSpeed(value);
    setAnchor(null);
    setError(false);
  };

  const popperContainerRef = useClickOutside(
    () => {
      setOpen(false);
      setAnchor(null);
      setSpeed('');
      setError(false);
    },
    ['mousedown'],
    null,
  );

  const checkMoreThanTwoDecimalDigits = (number) =>
    Number.isInteger(number * 100);

  const checkInValidSpeed = (value) =>
    value < VALID_SPEED.min || value > VALID_SPEED.max;

  const getValidSpeed = (value) => {
    const speedNumber = Number(value);
    if (checkInValidSpeed(value)) return currentSpeed;
    if (!checkMoreThanTwoDecimalDigits(speedNumber))
      return parseFloat(parseFloat(value).toFixed(2));
    return speedNumber;
  };

  const handleValidateSpeed = (value) => {
    const isNumber = checkValidNumber(value);
    if (!isNumber) {
      setError(true);
      return;
    }
    if (checkInValidSpeed(value)) {
      setError(true);
      return;
    }

    const validSpeed = getValidSpeed(value);
    handleSelectedValue(validSpeed);
    setError(false);
  };

  useEffect(() => {
    setTextValue(getTextValue(currentSpeed));
  }, [language]);

  useEffect(() => {
    setTextValue(getTextValue(currentSpeed));
  }, [currentSpeed]);

  const handleDropdownClick = (event) => {
    if (disabled) return;
    setAnchor(event.currentTarget);
    setOpen(!open);
    if (open && inputRef.current) {
      inputRef.current?.focus();
    }
  };

  const handleChangeSpeed = (e) => {
    const { value } = e.target;
    const newValue = replaceNumberWithDot(value);
    setSpeed(newValue);
  };

  const renderArrow = (anchorDropdown) => {
    if (anchorDropdown) {
      return (
        <ArrowDropUpRoundedIcon
          sx={{
            color: disabled ? TRANSPARENT_COLOR.lightGray : COLOR.navyBlue,
            fontSize: '24px',
          }}
        />
      );
    }
    return (
      <ArrowDropDownRoundedIcon
        sx={{
          color: disabled ? TRANSPARENT_COLOR.lightGray : COLOR.navyBlue,
          fontSize: '24px',
        }}
      />
    );
  };

  return (
    <StyledSpeed
      isSentence={isSentence}
      active={open}
      disabled={disabled}
      width={width}
      data-speed={speed}
    >
      <Typography
        variant="body-14-medium"
        className="text"
        style={{ lineHeight: '24px' }}
        color={COLOR.darkGray}
      >
        {t('voiceSpeed')}
      </Typography>
      <div
        onClick={handleDropdownClick}
        role="button"
        tabIndex={0}
        className={`dropdown-container ${disabled ? 'disabled' : ''}`}
      >
        <Typography
          variant="body-14-medium"
          className="text"
          color={disabled ? TRANSPARENT_COLOR.lightGray : COLOR.indigo[80]}
        >
          {textValue}
        </Typography>
        {renderArrow(anchor)}
      </div>
      <Popover
        open={open}
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className="popover-break-time"
        PaperProps={{
          style: {
            marginTop: '8px',
            borderRadius: '8px',
            width: '184px',
            paddingBottom: '8px',
          },
          sx: {
            boxShadow: '0px 4px 16px 0px #00000014 !important',
          },
        }}
      >
        <div ref={popperContainerRef}>
          <Box
            sx={{
              padding: '8px 12px',
              borderBottom: `1px solid ${COLOR.indigo['16']}`,
              boxSizing: 'border-box',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body-14-medium" style={{ color: COLOR.black }}>
              {t('voiceSpeed')}
            </Typography>
          </Box>
          <StyledListMenuItem>
            {SPEED.map((option, idx) => (
              <PopoverMenuItem
                onClick={() => {
                  handleSelectedValue(option.value);
                  setTextValue(getTextValue(option.value));
                }}
                key={option.value}
                style={{
                  borderRadius: '8px',
                  height: '40px',
                }}
                className={`dropdown__menu_item item-${idx} ${
                  option.value === currentSpeed ? 'selected' : ''
                }`}
              >
                <Typography
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <div className="speed-value">{option.value}x</div>
                  {t(option.title)}
                </Typography>
              </PopoverMenuItem>
            ))}
          </StyledListMenuItem>
          <StyledCustomSpeed>
            <div className="custom-speed-wrapper">
              <TextField
                variant="outlined"
                size="small"
                className="text-field"
                placeholder={t('customize')}
                value={speed}
                onChange={handleChangeSpeed}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleValidateSpeed(speed);
                  }
                }}
                error={error}
                inputRef={inputRef}
                autoFocus
                sx={{
                  '& .Mui-error': {
                    borderColor: COLOR.errorDropZone,
                  },
                }}
              />
              <Box>
                <CommonButton
                  variant="containedSmall"
                  color="primary"
                  onClick={() => handleValidateSpeed(speed)}
                  customStyle={{
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    padding: '4px 8px !important',
                    width: '100%',
                    borderRadius: '8px',
                    height: '32px',
                    border: 'none',
                  }}
                  disabled={!speed}
                >
                  {t('apply')}
                </CommonButton>
              </Box>
            </div>
            {error && (
              <Typography
                variant="body-12-regular"
                className="error-text"
                color={COLOR.errorDropZone}
              >
                {t('validValuesSpeed', {
                  min: `${VALID_SPEED.min}`,
                  max: `${VALID_SPEED.max}`,
                })}
              </Typography>
            )}
          </StyledCustomSpeed>
        </div>
      </Popover>
    </StyledSpeed>
  );
};
export default SpeedNew;
